'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { LeadCaptureForm } from '@/components/calculator/LeadCaptureForm';

// Each option carries a risk weight (higher = more risk) and a gap flag that
// surfaces a specific recommendation when chosen.
interface Option {
  text: string;
  risk: number; // 0 (good) .. 3 (worst)
  gap?: string; // recommendation triggered when this option is selected
}

interface Question {
  id: string;
  text: string;
  helpText?: string;
  options: Option[];
}

const QUESTIONS: Question[] = [
  {
    id: 'dmarc_policy',
    text: 'Do you have a DMARC record, and what policy is it set to?',
    helpText: 'DMARC tells receivers what to do with mail that fails authentication.',
    options: [
      { text: 'Yes—enforced at p=reject', risk: 0 },
      { text: 'Yes—at p=quarantine', risk: 1, gap: 'You’re at p=quarantine. Move to p=reject once monitoring confirms legitimate mail passes—quarantine still lets some spoofed mail through.' },
      { text: 'Yes—but only p=none (monitoring)', risk: 2, gap: 'p=none blocks nothing. It’s a starting point; the goal is p=reject so spoofed mail is rejected outright.' },
      { text: 'No DMARC record / not sure', risk: 3, gap: 'With no DMARC record, anyone can send email as your domain. Publishing DMARC and enforcing it is the single biggest fix.' },
    ],
  },
  {
    id: 'spf',
    text: 'Is SPF configured for every sending domain?',
    helpText: 'SPF lists which servers may send mail for your domain.',
    options: [
      { text: 'Yes—SPF published and kept current', risk: 0 },
      { text: 'Yes, but it hasn’t been reviewed in a while', risk: 1, gap: 'Stale SPF drifts as you add SaaS senders. Review it so legitimate mail isn’t failing and you’re under the 10-lookup limit.' },
      { text: 'Only on the main domain, not all of them', risk: 2, gap: 'Unused and secondary domains without SPF/DMARC are prime spoofing targets. Cover every domain you own.' },
      { text: 'No SPF / not sure', risk: 3, gap: 'No SPF means receivers can’t verify your servers. Publish SPF as the foundation for DMARC.' },
    ],
  },
  {
    id: 'dkim',
    text: 'Is DKIM signing enabled for your outbound mail?',
    helpText: 'DKIM cryptographically signs your mail so receivers can verify it.',
    options: [
      { text: 'Yes—all mail streams are DKIM-signed', risk: 0 },
      { text: 'Some streams (e.g. M365) but not all SaaS senders', risk: 2, gap: 'Unsigned streams (marketing, ticketing, CRM) fail DMARC alignment. DKIM-sign every legitimate sender.' },
      { text: 'No DKIM / not sure', risk: 3, gap: 'Without DKIM, DMARC relies on SPF alignment alone and breaks on forwarding. Enable DKIM on all senders.' },
    ],
  },
  {
    id: 'monitoring',
    text: 'Do you monitor DMARC aggregate (RUA) reports?',
    helpText: 'RUA reports show who is sending mail as your domain.',
    options: [
      { text: 'Yes—we review reports and act on them', risk: 0 },
      { text: 'Reports are collected but rarely reviewed', risk: 1, gap: 'Collecting RUA reports without reviewing them means you can’t safely enforce. Use a DMARC monitoring service.' },
      { text: 'No reporting address configured / not sure', risk: 2, gap: 'Without RUA reporting you’re blind to who’s sending as you. Add a reporting address and review it before enforcing.' },
    ],
  },
  {
    id: 'senders',
    text: 'Do you know every service that sends email as your domain?',
    helpText: 'Shadow senders break alignment and create gaps.',
    options: [
      { text: 'Yes—we maintain a current inventory of all senders', risk: 0 },
      { text: 'Mostly, but new SaaS tools get added without review', risk: 2, gap: 'Shadow senders break alignment. Inventory and authorize every sender.' },
      { text: 'No—too many tools to track', risk: 3, gap: 'Unknown senders make safe enforcement impossible. A managed service maps every legitimate sender before locking down the policy.' },
    ],
  },
  {
    id: 'lookalikes',
    text: 'Are you watching for lookalike / cousin domains used to impersonate you?',
    helpText: 'Attackers register similar domains to bypass DMARC entirely.',
    options: [
      { text: 'Yes—we monitor and act on lookalike domains', risk: 0 },
      { text: 'We’ve registered a few obvious variants', risk: 1, gap: 'Defensive registration helps, but attackers use new variants. Add lookalike-domain monitoring.' },
      { text: 'No monitoring in place / not sure', risk: 2, gap: 'Lookalike domains bypass your DMARC entirely. Monitoring surfaces impersonation domains before they’re used against your customers.' },
    ],
  },
];

type Status = 'intro' | 'assessment' | 'results';

interface Answer {
  questionId: string;
  optionIndex: number;
}

function levelFor(percent: number): {
  level: 'High' | 'Medium' | 'Low';
  color: string;
  bg: string;
  blurb: string;
} {
  if (percent >= 55) {
    return {
      level: 'High',
      color: 'text-red-700 dark:text-red-300',
      bg: 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800',
      blurb:
        'Your domain is highly spoofable—attackers can send email as you today. Publishing and enforcing DMARC, SPF, and DKIM should be an immediate priority.',
    };
  }
  if (percent >= 25) {
    return {
      level: 'Medium',
      color: 'text-amber-700 dark:text-amber-300',
      bg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800',
      blurb:
        'You have some email authentication in place but meaningful gaps remain. Closing them—and getting to DMARC p=reject—will materially cut your impersonation risk.',
    };
  }
  return {
    level: 'Low',
    color: 'text-green-700 dark:text-green-300',
    bg: 'bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800',
    blurb:
      'Your email authentication posture is strong. A managed service keeps it that way—monitoring senders, watching for impersonation, and holding you at enforcement as you add tools.',
  };
}

export default function RiskCheckClient() {
  const [status, setStatus] = useState<Status>('intro');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [domainCount, setDomainCount] = useState<number>(3);

  const handleAnswer = (optionIndex: number) => {
    const question = QUESTIONS[currentIndex];
    const newAnswer: Answer = { questionId: question.id, optionIndex };
    const newAnswers = [...answers.filter((a) => a.questionId !== question.id), newAnswer];
    setAnswers(newAnswers);

    if (currentIndex < QUESTIONS.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setStatus('results');
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const handleRestart = () => {
    setAnswers([]);
    setCurrentIndex(0);
    setStatus('intro');
  };

  const result = useMemo(() => {
    if (answers.length !== QUESTIONS.length) return null;
    let total = 0;
    const maxPerQuestion = 3;
    const gaps: string[] = [];
    QUESTIONS.forEach((q) => {
      const a = answers.find((ans) => ans.questionId === q.id);
      if (!a) return;
      const option = q.options[a.optionIndex];
      total += option.risk;
      if (option.gap) gaps.push(option.gap);
    });
    const max = QUESTIONS.length * maxPerQuestion;
    const percent = Math.round((total / max) * 100);
    return { percent, gaps, ...levelFor(percent) };
  }, [answers]);

  const resultsSummary = useMemo(() => {
    if (!result) return '';
    const answerLines = QUESTIONS.map((q) => {
      const a = answers.find((ans) => ans.questionId === q.id);
      const chosen = a ? q.options[a.optionIndex].text : 'N/A';
      return `  • ${q.text} → ${chosen}`;
    }).join('\n');

    const gapLines = result.gaps.length
      ? result.gaps.map((g, i) => `${i + 1}. ${g}`).join('\n')
      : 'No major gaps identified—maintain current controls.';

    return `DMARC SPOOFABILITY RISK CHECK RESULTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Spoofing Risk: ${result.level} (${result.percent}% risk score)
Sending domains: ${domainCount}

${result.blurb}

ANSWERS:
${answerLines}

TOP GAPS TO CLOSE:
${gapLines}`;
  }, [result, answers, domainCount]);

  // Intro
  if (status === 'intro') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">✉️</div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">How spoofable is your domain?</h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-lg mx-auto">
              Six questions on DMARC policy, SPF, DKIM, report monitoring, your sender inventory, and lookalike domains. Get an instant Spoofing Risk score and your top gaps.
            </p>
          </div>

          <div className="bg-[#2813e8]/5 border border-[#2813e8]/20 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-[#2813e8] mb-2">What you&apos;ll learn:</h3>
            <ul className="text-gray-700 dark:text-gray-300 text-sm space-y-1">
              <li>• Whether your spoofing risk is High, Medium, or Low</li>
              <li>• The specific gaps leaving you open to impersonation</li>
              <li>• What to fix first to safely reach DMARC p=reject</li>
            </ul>
          </div>

          <button
            onClick={() => setStatus('assessment')}
            className="w-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] hover:opacity-90 text-white font-semibold py-3 px-6 rounded-lg transition-opacity"
          >
            Start the Risk Check
          </button>
        </div>
      </div>
    );
  }

  // Assessment
  if (status === 'assessment') {
    const question = QUESTIONS[currentIndex];
    const progress = ((currentIndex + 1) / QUESTIONS.length) * 100;
    const currentAnswer = answers.find((a) => a.questionId === question.id);

    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span>DMARC Risk Check</span>
              <span>Question {currentIndex + 1} of {QUESTIONS.length}</span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">{question.text}</h2>
            {question.helpText && <p className="text-gray-600 dark:text-gray-400 text-sm">{question.helpText}</p>}
          </div>

          <div className="space-y-3 mb-6">
            {question.options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleAnswer(idx)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  currentAnswer?.optionIndex === idx
                    ? 'border-[#2813e8] bg-[#2813e8]/5'
                    : 'border-gray-200 dark:border-gray-700 hover:border-[#2813e8]/40 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <span className="text-gray-900 dark:text-gray-100">{option.text}</span>
              </button>
            ))}
          </div>

          <div className="flex justify-between">
            <button
              onClick={handleBack}
              disabled={currentIndex === 0}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Back
            </button>
            <button
              onClick={handleRestart}
              className="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Start Over
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Results
  if (status === 'results' && result) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Score */}
        <div className={`rounded-2xl border-2 p-8 ${result.bg}`}>
          <div className="text-center mb-6">
            <div className="text-5xl mb-4">{result.level === 'High' ? '🚨' : result.level === 'Medium' ? '⚠️' : '✅'}</div>
            <h2 className={`text-3xl font-bold mb-2 ${result.color}`}>Spoofing Risk: {result.level}</h2>
            <p className="text-gray-700 dark:text-gray-300 max-w-xl mx-auto">{result.blurb}</p>
          </div>
          <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-4 text-center">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Risk score</div>
            <div className={`text-3xl font-bold ${result.color}`}>{result.percent}%</div>
          </div>
        </div>

        {/* Gaps */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
            {result.gaps.length ? 'Top Gaps to Close' : 'No Major Gaps Identified'}
          </h3>
          {result.gaps.length ? (
            <ul className="space-y-3">
              {result.gaps.map((gap, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#2813e8]/10 text-xs font-bold text-[#2813e8]">{i + 1}</span>
                  <span className="text-gray-700 dark:text-gray-300 leading-relaxed">{gap}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600 dark:text-gray-400">Your controls look solid. A managed service keeps sender inventory, monitoring, and enforcement tight as you add tools.</p>
          )}
        </div>

        {/* Domain count */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            How many domains do you send email from? <span className="text-[#2813e8] font-bold">{domainCount}</span>
          </label>
          <input
            type="range"
            min="1"
            max="100"
            step="1"
            value={domainCount}
            onChange={(e) => setDomainCount(Number(e.target.value))}
            aria-label="Number of sending domains"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#2813e8]"
          />
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>1</span>
            <span>50</span>
            <span>100</span>
          </div>
        </div>

        {/* Offer CTA */}
        <div className="rounded-2xl border-2 border-[#2813e8]/30 bg-[#2813e8]/5 p-6 text-center">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Want these gaps closed for you?</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Inventive HQ deploys and manages DMARC, SPF, and DKIM—moving you to p=reject without breaking a single legitimate email.
          </p>
          <Link
            href="/services/dmarc-management"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] px-8 py-3 font-semibold text-white shadow-lg transition-all hover:scale-105"
          >
            See Managed DMARC
          </Link>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleRestart}
            className="flex-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium py-3 px-6 rounded-lg transition-colors"
          >
            🔄 Retake Check
          </button>
        </div>

        {/* Lead capture */}
        <div className="mt-8">
          <LeadCaptureForm
            source="dmarc"
            title="Get Your Domain's Spoofing Risk Reviewed by an Expert"
            description="Our team will review your results and send a prioritized plan to safely reach DMARC p=reject."
            deliverable="a prioritized DMARC/SPF/DKIM hardening plan"
            resultsSummary={resultsSummary}
          />
        </div>
      </div>
    );
  }

  return null;
}
